import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Page, PageHeader, Section } from '../../../components/layout/primitives';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import {
  featureService,
  type FeatureDetail as FeatureDetailType,
  type AuditLogEntry,
  type FeatureCategory,
  type FeatureType,
} from '../../../services/featureService';
import FeatureModal from './components/FeatureModal';
import { Spinner } from '../../../components/ui/Button';
import { Table } from '../../../components/ui/Table';

const TYPE_LABELS: Record<FeatureType, string> = { boolean: 'Boolean', limit: 'Limit', option: 'Option' };
const CATEGORY_LABELS: Record<FeatureCategory, string> = {
  access: 'Access', capacity: 'Capacity', workflow: 'Workflow',
  reporting: 'Reporting', support: 'Support', integration: 'Integration',
};

const ACTION_LABELS: Record<AuditLogEntry['action'], string> = {
  created: 'Created', updated: 'Updated', archived: 'Archived', restored: 'Restored', deleted: 'Deleted',
};

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (v === -1) return 'Unlimited';
  if (typeof v === 'boolean') return v ? 'On' : 'Off';
  return String(v);
}

const FeatureDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [feature, setFeature] = useState<FeatureDetailType | null>(null);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const AUDIT_PAGE_SIZE = 10;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const detail = await featureService.getById(id);
      setFeature(detail);
    } catch {
      // apiClient already surfaces a toast for the failed request.
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadAudit = useCallback(async () => {
    if (!id) return;
    try {
      const res = await featureService.getAuditLog(id, auditPage, AUDIT_PAGE_SIZE);
      setAuditLogs(res.logs);
      setAuditTotal(res.total);
    } catch {
      // apiClient already surfaces a toast for the failed request.
    }
  }, [id, auditPage]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  const auditTotalPages = Math.ceil(auditTotal / AUDIT_PAGE_SIZE);

  // Group per-plan values by Pod so the "Used by" table reads Pod → its plans, rather than
  // a flat list — a Pod's Starter/Growth/Scale plans render as one row per plan.
  const podGroups = React.useMemo(() => {
    if (!feature) return [];
    const planUsage = feature.planUsage ?? [];
    const byPod = new Map<string, { podId: string; podName: string; plans: typeof planUsage }>();
    for (const row of planUsage) {
      if (!byPod.has(row.podId)) byPod.set(row.podId, { podId: row.podId, podName: row.podName, plans: [] });
      byPod.get(row.podId)!.plans.push(row);
    }
    const statusByPod = new Map((feature.podsUsing ?? []).map((p) => [p.podId, p.status]));
    return [...byPod.values()].map((g) => ({ ...g, status: statusByPod.get(g.podId) ?? 'active' }));
  }, [feature]);

  if (loading || !feature) {
    return (
      <Page>
        <Section>
          <div className="flex items-center gap-2 py-16 justify-center text-agentic-text-secondary text-sm">
            <Spinner className="h-4 w-4 animate-spin rounded-full border-2 border-agentic-text-secondary border-t-transparent" />
            Loading…
          </div>
        </Section>
      </Page>
    );
  }

  const isLocked = (field: string) => (feature?.lockedFields ?? []).includes(field);

  return (
    <Page>
      <PageHeader
        title={
          <div className="flex items-center gap-3">
            <Button tone="muted-text" onClick={() => navigate('/super-features')}>
              <ArrowLeftIcon className="h-5 w-5" />
            </Button>
            {feature.name}
          </div>
        }
        subtitle={<code className="text-xs">{feature.key}</code>}
        aside={<Button size="sm" onClick={() => setEditOpen(true)}>Edit</Button>}
      />

      <Section className="space-y-6">
        {/* Fields */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold text-agentic-text-primary mb-4">Details</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <Field label="Type" locked={isLocked('type')}>{TYPE_LABELS[feature.type]}</Field>
            <Field label="Category">{CATEGORY_LABELS[feature.category]}</Field>
            <Field label="Description">{feature.description || '—'}</Field>
            <Field label="Default value">{formatValue(feature.defaultValue)}</Field>
            {feature.type === 'limit' && (
              <>
                <Field label="Unit" locked={isLocked('unit')}>{feature.unit || '—'}</Field>
                <Field label="Unlimited allowed" locked={isLocked('unlimitedAllowed')}>{feature.unlimitedAllowed ? 'Yes' : 'No'}</Field>
                <Field label="Min / Max">
                  {feature.minValue ?? '—'} / {feature.maxValue ?? '—'}
                </Field>
              </>
            )}
            {feature.type === 'option' && (
              <Field label="Options">
                {(feature.options || []).map((o) => o.label).join(', ') || '—'}
              </Field>
            )}
            <Field label="Enforced by">{feature.enforcedBy}</Field>
            <Field label="Show on pricing page">{feature.showOnPricingPage ? 'Yes' : 'No'}</Field>
            <Field label="Status">{feature.status === 'active' ? 'Active' : 'Archived'}</Field>
            <Field label="Used by">{feature.podCount} Pod{feature.podCount !== 1 ? 's' : ''}</Field>
          </dl>
        </Card>

        {/* Used by */}
        <Card className="overflow-hidden p-0">
          <h3 className="text-sm font-semibold text-agentic-text-primary px-5 pt-5 pb-3">Used by</h3>
          {podGroups.length === 0 ? (
            <p className="px-5 pb-5 text-sm text-agentic-text-secondary">Not used by any Pod yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table className="w-full text-sm">
                <thead>
                  <tr className="border-y border-agentic-gray-100 bg-agentic-gray-50 text-left">
                    <th className="py-2.5 px-5 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Pod</th>
                    <th className="py-2.5 px-5 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Status</th>
                    <th className="py-2.5 px-5 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Plan</th>
                    <th className="py-2.5 px-5 text-xs font-semibold text-agentic-text-secondary uppercase tracking-wide">Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-agentic-gray-100">
                  {podGroups.map((g) =>
                    g.plans.map((p, i) => (
                      <tr key={p.planId}>
                        {i === 0 && (
                          <td className="py-2.5 px-5 font-medium text-agentic-text-primary" rowSpan={g.plans.length}>
                            {g.podName}
                          </td>
                        )}
                        {i === 0 && (
                          <td className="py-2.5 px-5 text-xs text-agentic-text-secondary capitalize" rowSpan={g.plans.length}>
                            {g.status}
                          </td>
                        )}
                        <td className="py-2.5 px-5 text-agentic-text-secondary">{p.planName}</td>
                        <td className="py-2.5 px-5 font-mono text-xs">{formatValue(p.value)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card>

        {/* Change history */}
        <Card className="overflow-hidden p-0">
          <h3 className="text-sm font-semibold text-agentic-text-primary px-5 pt-5 pb-3">Change history</h3>
          {auditLogs.length === 0 ? (
            <p className="px-5 pb-5 text-sm text-agentic-text-secondary">No changes recorded yet.</p>
          ) : (
            <div className="divide-y divide-agentic-gray-100">
              {auditLogs.map((log) => (
                <div key={log.id} className="px-5 py-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-agentic-text-primary">{ACTION_LABELS[log.action]}</span>
                    <span className="text-xs text-agentic-text-secondary">
                      {new Date(log.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                  <p className="text-xs text-agentic-text-secondary mt-0.5">{log.actorEmail}</p>
                  {'record' in log.changes ? null : (
                    <ul className="mt-1.5 space-y-0.5 text-xs text-agentic-text-secondary">
                      {Object.entries(log.changes).map(([field, diff]: [string, any]) => (
                        <li key={field}>
                          <span className="font-medium">{field}</span>: {formatValue(diff.from)} → {formatValue(diff.to)}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
          {auditTotalPages > 1 && (
            <div className="flex items-center justify-between border-t border-agentic-gray-100 px-5 py-3">
              <p className="text-xs text-agentic-text-secondary">{auditTotal} total</p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" disabled={auditPage <= 1} onClick={() => setAuditPage((p) => p - 1)}>← Prev</Button>
                <span className="text-xs text-agentic-text-secondary px-1">{auditPage} / {auditTotalPages}</span>
                <Button size="sm" variant="ghost" disabled={auditPage >= auditTotalPages} onClick={() => setAuditPage((p) => p + 1)}>Next →</Button>
              </div>
            </div>
          )}
        </Card>
      </Section>

      {editOpen && (
        <FeatureModal
          feature={feature}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}
        />
      )}
    </Page>
  );
};

const Field: React.FC<{ label: string; locked?: boolean; children: React.ReactNode }> = ({ label, locked, children }) => (
  <div>
    <dt className="text-xs font-medium text-agentic-text-secondary uppercase tracking-wide flex items-center gap-1.5">
      {label}
      {locked && <span className="text-amber-500 normal-case font-normal">locked</span>}
    </dt>
    <dd className="mt-1 text-agentic-text-primary">{children}</dd>
  </div>
);

export default FeatureDetail;
